import React, { useState } from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";
import SabiasQue from "../SabiasQue";
import ImagenNoDisponibleKawaii from "../ImagenNoDisponibleKawaii";

// Template57: Página de Ginza de Tokio
// Copia DIRECTA del componente Page18.jsx de guia-tokio, adaptado para usar las props del sistema guia
const Template57 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
	updatePage,
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;
	const [palacioError, setPalacioError] = useState(false);
	const [palacio2Error, setPalacio2Error] = useState(false);
	const [ginzaError, setGinzaError] = useState(false);
	const [palacioStickerError, setPalacioStickerError] = useState(false);
	const [ginzaStickerError, setGinzaStickerError] = useState(false);

	const headerText = page.texts?.[0]?.content;
	const logoImage = page.canvasImages?.find(
		(img) => img.id === "logo",
	)?.imageUrl;

	// Lógica especial para la guía de Tokio: header #24221a en primera página, #e24217 en las siguientes
	const pageOrder =
		page.order !== undefined
			? page.order
			: page.data?.order !== undefined
				? page.data.order
				: -1;
	const isTokioGuide = headerBackgroundColor === "#24221a";
	const actualHeaderColor =
		isTokioGuide && pageOrder > 0 ? "#e24217" : headerBackgroundColor;

	const getImageSrc = (id) => {
		if (page.images && Array.isArray(page.images)) {
			const found = page.images.find((img) => img?.id === id);
			if (found && found.src) return found.src;
		}
		return undefined;
	};

	const getCanvasImageUrl = (id) => {
		if (page.canvasImages && Array.isArray(page.canvasImages)) {
			const found = page.canvasImages.find((img) => img?.id === id);
			if (found && found.imageUrl) return found.imageUrl;
		}
		return undefined;
	};

	const palacioImage = getImageSrc("palacio");
	const palacio2Image = getImageSrc("palacio-2");
	const ginzaImage = getImageSrc("ginza-main");
	const ginzaShopImage = getImageSrc("ginza-shop");
	const palacioSticker = getCanvasImageUrl("imperial-sticker");
	const ginzaSticker = getCanvasImageUrl("ginza-sticker");

	const introText = page.texts?.[1]?.content;
	const heroTitle = page.texts?.[2]?.content;
	const palacioTitle = page.texts?.[3]?.content;
	const palacioParagraph = page.texts?.[4]?.content;
	const sabiasQuePalacioHtml = page.texts?.[5]?.content;
	const estacionTitle = page.texts?.[6]?.content;
	const estacionText = page.texts?.[7]?.content;
	const sabiasQueEstacionHtml = page.texts?.[8]?.content;
	const ginzaTitle = page.texts?.[9]?.content;
	const ginzaParagraph = page.texts?.[10]?.content;
	const sabiasQueGinzaHtml = page.texts?.[11]?.content;
	const comoLlegarTitle = page.texts?.[12]?.content;
	const comoLlegarContent = page.texts?.[13]?.content;
	const stickerWakoText = page.texts?.[14]?.content;

	const parseSabiasQueItem = (html) => {
		if (!html) return null;
		const parser = new DOMParser();
		const doc = parser.parseFromString(html, "text/html");
		const p = doc.querySelector("p");
		if (!p) return null;
		const text = (p.textContent ?? p.innerText ?? "").trim();
		const emojiMatch = text.match(/^([^\s]+)\s+(.*)/s);
		if (emojiMatch)
			return { icono: emojiMatch[1], texto: emojiMatch[2].trim() };
		return null;
	};
	const sabiasQuePalacio = parseSabiasQueItem(sabiasQuePalacioHtml);
	const sabiasQueEstacion = parseSabiasQueItem(sabiasQueEstacionHtml);
	const sabiasQueGinza = parseSabiasQueItem(sabiasQueGinzaHtml);

	// Estilos reutilizables
	const estiloH3 = {
		fontFamily: `${fontFamily} !important`,
		fontSize: "14px",
		fontWeight: 700,
		textTransform: "uppercase",
		margin: 0,
	};

	const contentColor = textColor || "#444";

	const estiloP = {
		fontFamily: `${fontFamily} !important`,
		fontSize: "13px",
		color: contentColor,
		margin: 0,
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				height: "auto",
				width: "100%",
				backgroundColor,
				color: contentColor,
				position: "relative",
				fontFamily: `${fontFamily} !important`,
				"& *:not([data-template-header])": {
					fontFamily: `${fontFamily} !important`,
					color: "inherit",
				},
				"& p": {
					marginTop: "0px !important",
					marginBottom: "10px !important",
				},
			}}
		>
			{/* CABECERA */}
			<Box
				data-block-id="header"
				data-template-header
				sx={{
					backgroundColor: actualHeaderColor,
					color: headerTextColor,
					textAlign: "center",
					padding: "15px 0",
					fontFamily: `${fontFamily}`,
					fontSize: "14px",
					fontWeight: 800,
					flexShrink: 0,
					position: "sticky",
					top: 0,
					zIndex: 1053,
				}}
			>
				{headerText}
			</Box>

			{/* PORTADA */}
			<Box data-block-id="image-0" sx={{ position: "relative" }}>
				{palacioImage && !palacioError ? (
					<img
						src={palacioImage}
						alt="Palacio"
						onError={() => setPalacioError(true)}
						style={{ width: "100%", height: 200, objectFit: "cover" }}
					/>
				) : (
					<ImagenNoDisponibleKawaii
						width="100%"
						height={200}
						fontFamily={fontFamily}
					/>
				)}
				{heroTitle && (
					<Box
						component="h1"
						sx={{
							fontFamily: `${fontFamily} !important`,
							fontSize: isSmallScreen ? 28 : 32,
							textAlign: "center",
							position: "absolute",
							bottom: 0,
							margin: "10px",
							left: "20px",
							color: "#fff",
							textShadow: "2px 2px 4px rgba(0,0,0,0.6)",
						}}
					>
						{heroTitle}
					</Box>
				)}
				{palacioSticker && !palacioStickerError && (
					<img
						src={palacioSticker}
						alt=""
						onError={() => setPalacioStickerError(true)}
						style={{ width: 90, position: "absolute", bottom: 60, left: 18 }}
					/>
				)}
			</Box>

			{introText && (
				<Box data-block-id="text-1" sx={{ padding: "10px 12px", paddingBottom: 0 }}>
					<Box
						component="p"
						sx={{
							fontFamily: `${fontFamily} !important`,
							fontSize: 13,
							color: "#333",
							margin: 0,
							marginBottom: "0px!important",
						}}
					>
						{introText}
					</Box>
				</Box>
			)}

			{/* BLOQUES */}
			<Box
				sx={{
					padding: "10px 12px",
					display: "flex",
					flexDirection: "column",
					gap: "12px",
					marginTop: "-8px!important",
				}}
			>
				{/* Palacio Imperial */}
				<Box data-block-id="text-2" sx={{ display: "flex", gap: "16px", position: "relative" }}>
					{palacio2Image &&
						(!palacio2Error ? (
							<img
								src={palacio2Image}
								alt="Palacio"
								onError={() => setPalacio2Error(true)}
								style={{
									width: "40%",
									borderRadius: "12px",
									objectFit: "cover",
									height: 110,
								}}
							/>
						) : (
							<ImagenNoDisponibleKawaii
								width="40%"
								height={110}
								fontFamily={fontFamily}
								compact
							/>
						))}
					<Box sx={{ width: "60%" }}>
						{palacioTitle && (
							<Box component="h3" sx={estiloH3}>
								{palacioTitle}
							</Box>
						)}
						{palacioParagraph && (
							<Box component="p" sx={estiloP}>
								{palacioParagraph}
							</Box>
						)}
						{sabiasQuePalacio && (
							<Box sx={{ position: "absolute", bottom: -5, left: "32%" }}>
								<SabiasQue
									icono={sabiasQuePalacio.icono}
									texto={sabiasQuePalacio.texto}
								/>
							</Box>
						)}
					</Box>
				</Box>

				{/* Estación de Tokio */}
				<Box
					data-block-id="text-3"
					sx={{
						backgroundColor: "#ffe3cc",
						padding: "12px",
						borderRadius: "12px",
						display: "flex",
						gap: "16px",
						alignItems: "center",
						position: "relative",
					}}
				>
					<Box sx={{ width: "100%" }}>
						{estacionTitle && (
							<Box component="h3" sx={estiloH3}>
								{estacionTitle}
							</Box>
						)}
						{estacionText && (
							<Box
								component="div"
								dangerouslySetInnerHTML={{ __html: estacionText }}
								sx={estiloP}
							/>
						)}
					</Box>
					{sabiasQueEstacion && (
						<Box sx={{ position: "absolute", bottom: -20, right: "30%" }}>
							<SabiasQue
								icono={sabiasQueEstacion.icono}
								texto={sabiasQueEstacion.texto}
							/>
						</Box>
					)}
				</Box>

				{/* Ginza */}
				<Box data-block-id="text-4" sx={{ display: "flex", gap: "16px" }}>
					{ginzaImage &&
						(!ginzaError ? (
							<img
								src={ginzaImage}
								alt="Ginza"
								onError={() => setGinzaError(true)}
								style={{
									width: "40%",
									borderRadius: "12px",
									objectFit: "cover",
									height: 110,
								}}
							/>
						) : (
							<ImagenNoDisponibleKawaii
								width="40%"
								height={110}
								fontFamily={fontFamily}
								compact
							/>
						))}
					<Box sx={{ width: "60%", position: "relative" }}>
						{ginzaTitle && (
							<Box component="h3" sx={estiloH3}>
								{ginzaTitle}
							</Box>
						)}
						{ginzaParagraph && (
							<Box
								component="div"
								dangerouslySetInnerHTML={{ __html: ginzaParagraph }}
								sx={estiloP}
							/>
						)}
						{sabiasQueGinza && (
							<SabiasQue
								icono={sabiasQueGinza.icono}
								texto={sabiasQueGinza.texto}
							/>
						)}
					</Box>
				</Box>

				{/* CÓMO LLEGAR */}
				<Box
					data-block-id="text-5"
					sx={{
						backgroundColor: "#ffe3cc",
						border: "3px solid #f28b35",
						borderRadius: "12px",
						padding: "12px 12px",
						paddingBottom: "0px",
						fontSize: 12,
						fontFamily: `${fontFamily} !important`,
						color: "#333",
						position: "relative",
						width: isSmallScreen ? "60%" : "100%",
					}}
				>
					{comoLlegarTitle && (
						<Box
							component="h4"
							sx={{
								margin: 0,
								marginBottom: "8px",
								fontSize: 13,
								fontWeight: 700,
								color: "#f28b35!important",
								textTransform: "uppercase",
								fontFamily: `${fontFamily} !important`,
							}}
						>
							{comoLlegarTitle}
						</Box>
					)}
					{comoLlegarContent && (
						<Box
							component="div"
							dangerouslySetInnerHTML={{ __html: comoLlegarContent }}
							sx={{ ...estiloP, width: "64%" }}
						/>
					)}
					{ginzaSticker && !ginzaStickerError && (
						<img
							src={ginzaSticker}
							alt=""
							onError={() => setGinzaStickerError(true)}
							style={{
								width: 100,
								position: "absolute",
								bottom: isSmallScreen ? 0 : -20,
								right: isSmallScreen ? -150 : -34,
							}}
						/>
					)}
					{stickerWakoText && (
						<Box
							sx={{
								position: "absolute",
								bottom: isSmallScreen ? "70px" : "37px",
								right: isSmallScreen ? -120 : "20px",
								backgroundColor: "#fff8db",
								border: "2px solid #f5c400",
								borderRadius: "16px 16px 0px 16px",
								padding: "10px 12px",
								fontSize: 11,
								fontFamily: `${fontFamily} !important`,
								color: contentColor,
								boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
								maxWidth: 150,
							}}
						>
							{stickerWakoText}
						</Box>
					)}
				</Box>
			</Box>
		</Box>
	);
};

Template57.thumbnail = "/images/template-1.png";

export default Template57;
